/*
 * Gourmet Easy store finder (sections/ge-store-finder.liquid).
 *
 * The section renders every store as an <li> carrying its coordinates. This
 * file adds the map and re-orders those same elements by distance once the
 * visitor gives a location -- it never builds its own copy of the list.
 *
 * Leaflet (vendored, assets/leaflet.js) is loaded only when the map is about
 * to be seen: on wide screens when it scrolls near the viewport, on phones
 * when "Show map" is pressed. Sorting works without it, so a slow or failed
 * map load never blocks finding a store.
 *
 * A typed ZIP or city is looked up once, on submit, with OpenStreetMap's
 * Nominatim. Its usage policy forbids search-as-you-type and asks for
 * attribution, both of which the section honours. The visitor's position is
 * held in memory for this page view only and never stored or sent anywhere
 * except, for a typed place, that single lookup.
 */
if (!customElements.get('ge-store-finder')) {
  const STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
    FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
    MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
    NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
    NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
    PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
    WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  };
  const NOMINATIM = 'https://nominatim.openstreetmap.org/search';
  const TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  const ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors';
  // How many of the nearest stores the map zooms to after a search, so the
  // visitor sees their options and not just the single closest pin.
  const NEAREST_IN_VIEW = 3;

  let leafletPromise;
  const loadLeaflet = () => {
    if (window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;
    const urls = window.GE_LEAFLET || {};
    // The map must not be built before leaflet.css applies: Leaflet measures
    // its container and positions its panes on creation, and without the
    // stylesheet those measurements are wrong and every later fitBounds lands
    // off-centre. So both files have to finish, not just the script.
    const load = (el) =>
      new Promise((resolve, reject) => {
        el.onload = resolve;
        el.onerror = reject;
        document.head.appendChild(el);
      });
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = urls.css;
    const js = document.createElement('script');
    js.src = urls.js;
    leafletPromise = Promise.all([load(css), load(js)])
      .then(() => window.L)
      .catch((err) => {
        leafletPromise = null;
        throw err;
      });
    return leafletPromise;
  };

  const miles = (a, b) => {
    const rad = (d) => (d * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const h =
      Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 3958.8 * 2 * Math.asin(Math.sqrt(h));
  };

  const formatMiles = (d) => (d < 10 ? d.toFixed(1) : Math.round(d).toLocaleString('en-US')) + ' mi';

  customElements.define(
    'ge-store-finder',
    class GeStoreFinder extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-sf-form]');
        this.query = this.querySelector('[data-sf-query]');
        this.status = this.querySelector('[data-sf-status]');
        this.resetBtn = this.querySelector('[data-sf-reset]');
        this.locateBtn = this.querySelector('[data-sf-locate]');
        this.list = this.querySelector('[data-sf-list]');
        this.mapWrap = this.querySelector('[data-sf-map-wrap]');
        this.mapEl = this.querySelector('[data-sf-map]');
        this.mapToggle = this.querySelector('[data-sf-map-toggle]');
        this.allText = this.status ? this.status.textContent.trim() : '';
        this.origin = null;

        this.stores = [...this.querySelectorAll('[data-sf-store]')]
          .map((li) => ({
            li,
            lat: parseFloat(li.dataset.lat),
            lng: parseFloat(li.dataset.lng),
            name: li.dataset.name,
            state: STATE_NAMES[li.dataset.state] || li.dataset.state,
            city: li.dataset.city,
            dist: null,
          }))
          .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));

        // Entries come back in admin order. Alphabetical by state, then town,
        // is the order people scan a store list in before they search.
        this.byState = [...this.stores].sort(
          (a, b) =>
            a.state.localeCompare(b.state) || a.city.localeCompare(b.city) || a.name.localeCompare(b.name)
        );
        this.render(this.byState);

        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.lookup();
        });
        this.resetBtn.addEventListener('click', () => this.reset());

        if ('geolocation' in navigator) {
          this.locateBtn.hidden = false;
          this.locateBtn.addEventListener('click', () => this.locate());
        }

        this.querySelectorAll('[data-sf-show]').forEach((btn) => {
          btn.hidden = false;
          btn.addEventListener('click', () => {
            const store = this.stores.find((s) => s.li.contains(btn));
            if (store) this.focusStore(store);
          });
        });

        this.mapToggle.hidden = false;
        this.mapToggle.addEventListener('click', () => this.setMapOpen(!this.mapWrap.classList.contains('is-open')));

        // Wide screens show the map beside the list from the start, so it
        // loads as it nears the viewport. Phones wait for "Show map".
        this.wide = window.matchMedia('(min-width: 750px)');
        const watch = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting) && this.wide.matches) {
              watch.disconnect();
              this.ensureMap();
            }
          },
          { rootMargin: '300px' }
        );
        watch.observe(this.mapWrap);
        this.wide.addEventListener('change', () => {
          if (this.wide.matches) this.ensureMap().then(() => this.map && this.map.invalidateSize());
        });
      }

      render(order) {
        order.forEach((s) => this.list.appendChild(s.li));
      }

      setStatus(text, isError) {
        this.status.textContent = text;
        this.status.classList.toggle('is-error', !!isError);
      }

      busy(on) {
        this.classList.toggle('is-busy', on);
        this.form.querySelectorAll('button').forEach((b) => (b.disabled = on));
      }

      async lookup() {
        const q = this.query.value.trim();
        if (!q) {
          this.setStatus('Type a ZIP code or a town and state, like "Salem, OR".', true);
          this.query.focus();
          return;
        }
        const params = new URLSearchParams({
          format: 'jsonv2',
          limit: '1',
          addressdetails: '1',
          countrycodes: 'us',
          'accept-language': 'en',
        });
        const zip = q.match(/^(\d{5})(?:-\d{4})?$/);
        if (zip) {
          params.set('postalcode', zip[1]);
          params.set('country', 'us');
          params.delete('countrycodes');
        } else {
          params.set('q', q);
        }

        this.busy(true);
        this.setStatus('Looking up ' + q + '…');
        try {
          const res = await fetch(NOMINATIM + '?' + params, { headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          const hits = await res.json();
          if (!hits.length) {
            this.setStatus(
              'We couldn’t find "' + q + '". Try a 5-digit ZIP code, or a town and state like "Salem, OR".',
              true
            );
            return;
          }
          const hit = hits[0];
          this.useOrigin({ lat: parseFloat(hit.lat), lng: parseFloat(hit.lon), label: this.placeLabel(hit, q) });
        } catch (err) {
          this.setStatus('The location search isn’t responding just now. The full list of stores is below.', true);
        } finally {
          this.busy(false);
        }
      }

      placeLabel(hit, fallback) {
        const a = hit.address || {};
        const town = a.city || a.town || a.village || a.hamlet || a.suburb || a.county;
        const parts = [town, a.state].filter(Boolean);
        let label = parts.join(', ');
        if (a.postcode && /^\d{5}/.test(fallback)) label = (label ? label + ' ' : '') + a.postcode;
        return label || fallback;
      }

      locate() {
        this.busy(true);
        this.setStatus('Finding your location…');
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.busy(false);
            this.useOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: 'your location' });
          },
          (err) => {
            this.busy(false);
            this.setStatus(
              err.code === err.PERMISSION_DENIED
                ? 'Location access is turned off for this site. Type your ZIP code or city instead.'
                : 'We couldn’t get your location. Type your ZIP code or city instead.',
              true
            );
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
        );
      }

      useOrigin(origin) {
        this.origin = origin;
        this.stores.forEach((s) => (s.dist = miles(origin, s)));
        const sorted = [...this.stores].sort((a, b) => a.dist - b.dist);
        this.render(sorted);
        this.stores.forEach((s) => {
          const tag = s.li.querySelector('[data-sf-dist]');
          tag.textContent = formatMiles(s.dist);
          tag.hidden = false;
        });
        this.list.classList.add('is-sorted');
        this.list.scrollTop = 0;

        const near = sorted[0];
        const from = origin.label === 'your location' ? 'your location' : origin.label;
        this.setStatus(
          'Nearest first, from ' + from + '. Closest: ' + near.name + ', ' + formatMiles(near.dist) + ' away.'
        );
        this.resetBtn.hidden = false;
        this.highlight(near, false);
        this.ensureMap().then(() => this.showOrigin(sorted));
      }

      reset() {
        this.origin = null;
        this.stores.forEach((s) => {
          s.dist = null;
          s.li.querySelector('[data-sf-dist]').hidden = true;
        });
        this.list.classList.remove('is-sorted');
        this.render(this.byState);
        this.list.scrollTop = 0;
        this.highlight(null);
        this.setStatus(this.allText);
        this.resetBtn.hidden = true;
        this.query.value = '';
        if (this.map) {
          if (this.originMarker) this.originMarker.remove();
          this.originMarker = null;
          this.fitAll();
        }
      }

      highlight(store, scroll) {
        this.stores.forEach((s) => s.li.classList.toggle('is-active', s === store));
        if (store && scroll) {
          // Scroll the list panel, not the page, where the list is its own
          // scroll area; on phones it is not, and the page scrolls instead.
          const li = store.li;
          if (this.list.scrollHeight > this.list.clientHeight + 4) {
            this.list.scrollTo({ top: li.offsetTop - this.list.offsetTop - 8, behavior: 'smooth' });
          }
        }
      }

      setMapOpen(open) {
        this.mapWrap.classList.toggle('is-open', open);
        this.mapToggle.setAttribute('aria-expanded', String(open));
        this.mapToggle.textContent = open ? 'Hide map' : 'Show map';
        if (open) {
          return this.ensureMap().then(() => {
            this.map.invalidateSize();
            if (this.origin) this.showOrigin([...this.stores].sort((a, b) => a.dist - b.dist));
          });
        }
        return Promise.resolve();
      }

      focusStore(store) {
        const go = () => {
          this.map.setView([store.lat, store.lng], Math.max(this.map.getZoom(), 12));
          store.marker.openPopup();
          this.highlight(store, true);
        };
        if (!this.wide.matches && !this.mapWrap.classList.contains('is-open')) {
          this.setMapOpen(true).then(go);
        } else {
          this.ensureMap().then(go);
        }
        if (!this.wide.matches) this.mapWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      ensureMap() {
        if (this.mapReady) return this.mapReady;
        this.mapReady = loadLeaflet()
          .then((L) => this.buildMap(L))
          .catch(() => {
            this.mapReady = null;
            this.mapWrap.classList.add('is-failed');
            return Promise.reject();
          });
        return this.mapReady;
      }

      buildMap(L) {
        this.L = L;
        // Wheel zoom stays off until the map is clicked, so scrolling the page
        // past it does not get caught and zoom the map instead.
        const map = L.map(this.mapEl, { scrollWheelZoom: false, zoomSnap: 0.25 });
        map.on('focus click', () => map.scrollWheelZoom.enable());
        map.on('blur mouseout', () => map.scrollWheelZoom.disable());
        L.tileLayer(TILES, { maxZoom: 19, attribution: ATTRIBUTION }).addTo(map);
        map.attributionControl.setPrefix(false);

        const icon = L.divIcon({
          className: 'ge-sf__pin',
          html: '<span></span>',
          iconSize: [26, 34],
          iconAnchor: [13, 34],
          popupAnchor: [0, -30],
        });

        this.stores.forEach((s) => {
          const addr = s.li.querySelector('.ge-sf__store-addr');
          const directions = s.li.querySelector('.ge-sf__store-links a');
          const popup =
            '<strong class="ge-sf__pop-name"></strong><span class="ge-sf__pop-addr"></span>' +
            (directions ? '<a class="ge-sf__pop-link" target="_blank" rel="noopener">Directions</a>' : '');
          const el = document.createElement('div');
          el.className = 'ge-sf__pop';
          el.innerHTML = popup;
          el.querySelector('.ge-sf__pop-name').textContent = s.name;
          el.querySelector('.ge-sf__pop-addr').innerHTML = addr ? addr.innerHTML : '';
          if (directions) el.querySelector('.ge-sf__pop-link').href = directions.href;

          s.marker = L.marker([s.lat, s.lng], { icon, title: s.name, alt: s.name, keyboard: true })
            .bindPopup(el)
            .addTo(map);
          s.marker.on('click', () => this.highlight(s, true));
        });

        this.map = map;
        this.fitAll();
        return map;
      }

      fitAll() {
        // The opening view is the lower 48. Fitting Alaska and Hawaii as well
        // zooms out to most of the northern hemisphere and shrinks every other
        // pin into one cluster; their pins are still there, a drag away, and
        // a search near them zooms to them like any other store.
        const mainland = this.stores.filter((s) => s.lat < 50 && s.lng > -130);
        const bounds = this.L.latLngBounds((mainland.length ? mainland : this.stores).map((s) => [s.lat, s.lng]));
        this.map.fitBounds(bounds, { padding: [24, 24] });
      }

      showOrigin(sorted) {
        if (!this.map || !this.origin) return;
        const L = this.L;
        const here = [this.origin.lat, this.origin.lng];
        if (this.originMarker) this.originMarker.setLatLng(here);
        else {
          this.originMarker = L.circleMarker(here, {
            radius: 8,
            weight: 3,
            color: '#fffdf9',
            fillColor: '#2f6fb5',
            fillOpacity: 1,
          })
            .bindTooltip(this.origin.label === 'your location' ? 'You are here' : this.origin.label)
            .addTo(this.map);
        }
        this.originMarker.setTooltipContent(this.origin.label === 'your location' ? 'You are here' : this.origin.label);
        const pts = [here, ...sorted.slice(0, NEAREST_IN_VIEW).map((s) => [s.lat, s.lng])];
        this.map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 12 });
      }
    }
  );
}
